import { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StationResultProps } from "@/lib/type";
import {
  calculateDeliveryPrice,
  DeliveryAvailability,
  DeliveryPrice,
  DeliverySelection,
} from "@/features/delivery/api/deliveryApi";
import { isOutOfRadius } from "@/features/delivery/lib/deliveryPresentation.js";
import DeliveryServiceBanner from "@/features/delivery/components/DeliveryServiceBanner";
import DeliveryPriceBreakdown from "@/features/delivery/components/DeliveryPriceBreakdown";
import DeliveryTerms from "@/features/delivery/components/DeliveryTerms";
import DeliveryAddressInput, { DeliveryAddressValue } from "@/features/delivery/components/DeliveryAddressInput";

interface DeliveryLegProps {
  type: "pickup" | "return";
  station?: StationResultProps;
  scheduledAt: string;
  rentalStartAt: string;
  rentalEndAt: string;
  currencySymbol: string;
  apiUrl?: string;
  availability?: DeliveryAvailability;
  initialValue?: DeliverySelection | null;
  onChange: (selection: DeliverySelection | null) => void;
}

const EMPTY_ADDRESS: DeliveryAddressValue = { address: "", lat: "", lng: "", remark: "" };

const COPY = {
  pickup: { title: "Deliver the car to my address", cta: "Calculate delivery price", add: "Add delivery to booking" },
  return: { title: "Collect the car from my address", cta: "Calculate collection price", add: "Add collection to booking" },
};

/**
 * A single, independently selectable delivery leg (pickup OR return).
 * Flow: include → enter address → Calculate price → explicit "Add to booking"
 * (commits the selection) → shown as Added with a Remove button. Nothing is
 * added to the order until the customer clicks Add.
 */
export default function DeliveryLeg({
  type,
  station,
  scheduledAt,
  rentalStartAt,
  rentalEndAt,
  currencySymbol,
  apiUrl,
  availability,
  initialValue,
  onChange,
}: DeliveryLegProps) {
  const [included, setIncluded] = useState(Boolean(initialValue));
  const [addressValue, setAddressValue] = useState<DeliveryAddressValue>(
    initialValue
      ? {
          address: initialValue.customer_address,
          lat: String(initialValue.customer_lat),
          lng: String(initialValue.customer_lng),
          remark: initialValue.customer_remark ?? "",
        }
      : EMPTY_ADDRESS,
  );
  const [price, setPrice] = useState<DeliveryPrice | null>(initialValue?.price ?? null);
  const [added, setAdded] = useState(Boolean(initialValue));
  const [termsAccepted, setTermsAccepted] = useState(Boolean(initialValue));
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  const selectionFrom = (nextPrice: DeliveryPrice): DeliverySelection => ({
    type,
    customer_address: addressValue.address,
    customer_remark: addressValue.remark || undefined,
    customer_lat: Number(addressValue.lat),
    customer_lng: Number(addressValue.lng),
    scheduled_at: scheduledAt,
    price: nextPrice,
  });

  const resetPricing = () => {
    setPrice(null);
    setAdded(false);
    onChange(null);
  };

  const toggleIncluded = (checked: boolean) => {
    setIncluded(checked);
    if (!checked) {
      setError("");
      resetPricing();
    }
  };

  const calculate = async () => {
    if (!station) {
      return;
    }
    setIsChecking(true);
    setError("");
    try {
      const result = await calculateDeliveryPrice({
        station_id: station.rw_station_id,
        type,
        scheduled_at: scheduledAt,
        rental_start_at: rentalStartAt,
        rental_end_at: rentalEndAt,
        customer_lat: Number(addressValue.lat),
        customer_lng: Number(addressValue.lng),
        apiUrl,
      });
      setPrice(result);
      // Calculating shows the price but does NOT add it yet — the customer must
      // confirm with "Add … to booking".
      setAdded(false);
      onChange(null);
    } catch (err) {
      const apiError = err as DeliveryAvailability;
      resetPricing();
      setError(
        isOutOfRadius(apiError)
          ? "Sorry, this address is out of our delivery area. Please try another address."
          : apiError?.message ?? "Delivery is not available for this address.",
      );
    } finally {
      setIsChecking(false);
    }
  };

  const addToBooking = () => {
    if (price && price.available) {
      onChange(selectionFrom(price));
      setAdded(true);
    }
  };

  const remove = () => {
    setAdded(false);
    onChange(null);
  };

  const canCalculate = Boolean(station && addressValue.address && addressValue.lat && addressValue.lng);

  return (
    <div className="rounded-md border border-neutral-200 p-3" data-testid={`delivery-leg-${type}`}>
      <label className="flex items-center gap-2 font-medium">
        <input
          type="checkbox"
          checked={included}
          onChange={(event) => toggleIncluded(event.target.checked)}
          aria-label={COPY[type].title}
        />
        <span>{COPY[type].title}</span>
      </label>

      {included && (
        <div className="mt-3 space-y-3">
          <DeliveryServiceBanner availability={availability} currencySymbol={currencySymbol} type={type} />

          <DeliveryAddressInput
            type={type}
            value={addressValue}
            onChange={(next) => {
              setAddressValue(next);
              // Any address change invalidates the quote and the added state.
              resetPricing();
            }}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          {price && price.available && (
            <div className="space-y-3">
              <DeliveryPriceBreakdown price={price} currencySymbol={currencySymbol} type={type} />
              <DeliveryTerms
                terms={price.terms_and_conditions}
                accepted={termsAccepted}
                onAcceptedChange={setTermsAccepted}
              />
              {added ? (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1 font-medium text-green-700">
                    <Check className="h-4 w-4" aria-hidden="true" /> Added to your booking
                  </span>
                  <Button type="button" variant="outline" onClick={remove}>
                    Remove
                  </Button>
                </div>
              ) : (
                <Button type="button" onClick={addToBooking}>
                  {COPY[type].add}
                </Button>
              )}
            </div>
          )}

          {!(price && price.available) && (
            <Button type="button" disabled={!canCalculate || isChecking} onClick={calculate}>
              {isChecking ? "Checking…" : COPY[type].cta}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
